import { Component, inject, Pipe, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ConfiguracionRecetas } from './configuracion-recetas/configuracion-recetas';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatStepperModule } from '@angular/material/stepper';
import { MatInputModule } from '@angular/material/input';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatTableModule } from '@angular/material/table';
import { MatFormFieldModule } from '@angular/material/form-field';
import { NuevaIndicacionDialog } from './nueva-indicacion-dialog/nueva-indicacion-dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AltaPersonalDialog } from './alta-personal-dialog/alta-personal-dialog';
import Swal from 'sweetalert2';
import { PlansService } from '../../core/services/plans.service';
import { UsuarioService } from '../../core/services/usuario.service';
import { IndicacionService } from '../../core/services/indicacion.service';
import { AuthService } from '../../core/services/auth.service';
import {
  ActualizarExpedienteConfiguracion,
  ConfiguracionExpedienteService,
} from '../../core/services/configuracion-expediente.service';
import { MatSpinner } from '@angular/material/progress-spinner';
import { ClinicaService } from '../../core/services/clinica.service';
import {
  ActualizarClinica,
  Clinica,
  ConfiguracionClinica,
  RedSocial,
  Subscripcion,
} from '../../core/interface/interface';
import { DatePipe } from '@angular/common';
import { RecetasPdf } from '../../core/services/recetas-pdf.service';
import { firstValueFrom, forkJoin, Observable } from 'rxjs';
import { ActualizarCedulaRequest, CedulaService } from '../../core/services/cedula.service';
import {
  cedulasSinDuplicados,
  PATRON_CEDULA,
  TIPOS_CEDULA,
} from '../../core/validators/cedula.validators';
import { NuevoMunicipioDialog } from './nuevo-municipio-dialog/nuevo-municipio-dialog';
import { MunicipioServices } from '../../core/services/municipio.service';
import { BarriosMunicipioDialog } from './barrios-municipio-dialog/barrios-municipio-dialog/barrios-municipio-dialog';
import { RedesSociales } from '../../core/services/redes-sociales.service';
import { RedesSocialesDialog } from './redes-sociales-dialog/redes-sociales-dialog/redes-sociales-dialog';

interface OpcionConfiguracion {
  titulo: string;
  descripcion: string;
  icono: string;
  accion: () => void;
}

@Component({
  selector: 'app-configuraciones',
  imports: [
    MatIconModule,
    MatIcon,
    MatButton,
    MatStepperModule,
    FormsModule,
    MatStepperModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatSelectModule,
    MatOptionModule,
    MatTableModule,
    MatTooltipModule,
    ConfiguracionRecetas,
    MatSpinner,
    DatePipe,
  ],
  templateUrl: './configuraciones.html',
  styleUrl: './configuraciones.scss',
})
export class Configuraciones {
  constructor(
    private dialog: MatDialog,
    private recetasPdf: RecetasPdf,
  ) {}
  private planService = inject(PlansService);
  private usuarioService = inject(UsuarioService);
  private redSocialService = inject(RedesSociales);
  private municipioService = inject(MunicipioServices);
  private indicacionService = inject(IndicacionService);
  private authService = inject(AuthService);
  private expedienteConfiguracionService = inject(ConfiguracionExpedienteService);
  private clinicaService = inject(ClinicaService);
  private _formBuilder = inject(FormBuilder);
  private cedulaService = inject(CedulaService);
  tiposCedula = TIPOS_CEDULA;
  private cedulasPerfilOriginales = new Map<
    string | number,
    { tipoCedula: string; cedula: string }
  >();

  @ViewChild(ConfiguracionRecetas)
  configuracionRecetas?: ConfiguracionRecetas;

  user: any;
  ngOnInit(): void {
    this.cargarUsuarioActual();
    this.expedienteForm = this.fb.group({
      prefijo: ['PAC', [Validators.required, Validators.maxLength(10)]],

      longitud: [6, [Validators.required, Validators.min(1), Validators.max(20)]],

      formato: ['{PREFIJO}-{YY}-{MM}-{CONSECUTIVO}', Validators.required],
    });
  }

  tieneRol(rol: string): boolean {
    return this.user?.rol?.nombre === rol;
  }

  cargarUsuarioActual(): void {
    const usuarioLocal = this.authService.getUser();

    if (!usuarioLocal?.id) {
      // console.error('No se encontró el ID del usuario.');

      Swal.fire({
        icon: 'error',
        title: 'Usuario no identificado',
        text: 'No fue posible obtener la información de tu usuario.',
        showConfirmButton: false,
        timer: 2000,
      });

      return;
    }

    this.usuarioService.obtenerUsuario(usuarioLocal.id).subscribe({
      next: (usuario) => {
        // console.log('Usuario obtenido del backend:', usuario);

        // Primero asignamos el usuario
        this.user = usuario;

        // Cargamos los datos del formulario
        this.cargarDatosPerfil();

        // Ahora sí podemos comprobar el rol
        this.cargarPersonal();
        this.cargarIndicaciones();
        this.cargarMunicipios();
        this.cargarPlan();
        this.cargarClinicaActual();
      },

      error: (error) => {
        // console.error('Error al obtener el usuario:', error);

        Swal.fire({
          icon: 'error',
          title: 'No se pudo cargar tu perfil',
          text: error?.error?.message ?? 'Ocurrió un error al obtener la información del usuario.',
          showConfirmButton: false,
          timer: 2000,
        });
      },
    });
  }

  cargarDatosPerfil(): void {
    if (!this.user) return;

    this.perfilForm.patchValue({
      nombre: this.user.nombre ?? '',
      apellidoPaterno: this.user.apellidoPaterno ?? '',
      apellidoMaterno: this.user.apellidoMaterno ?? '',
    });

    this.cedulasPerfil.clear();
    this.cedulasPerfilOriginales.clear();

    for (const c of this.user.cedulas ?? []) {
      this.cedulasPerfilOriginales.set(c.id, {
        tipoCedula: c.tipoCedula,
        cedula: String(c.cedula),
      });
      this.cedulasPerfil.push(this.crearGrupoCedula(c));
    }
  }

  cargarPersonal(): void {
    if (this.user?.rol?.nombre === 'ADMIN') {
      this.usuarioService.obtenerUsuarios().subscribe({
        next: (usuarios) => {
          // console.log('Usuarios obtenidos:', usuarios);
          this.personalAgregado = usuarios.filter((u) => u.id !== this.user?.id);
        },
        error: (error) => {
          // console.error('Error al obtener el personal:', error);

          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: error?.error?.message ?? 'No se pudo cargar el personal.',
            showConfirmButton: false,
            timer: 2000,
          });
        },
      });
    }
  }

  cargarClinicaActual(): void {
    if (this.user?.rol?.nombre === 'ADMIN') {
      this.clinicaService.obtenerClinica().subscribe({
        next: (clinica) => {
          // console.log('Clínica obtenida del backend:', clinica);

          // Primero asignamos el usuario
          this.clinica = clinica;

          this.suscripcionActual = clinica.subscripciones?.[0];

          this.configuracionClinica = clinica.configuracion ?? undefined;

          this.redSocialAgregada = clinica.redesSociales ?? [];

          this.configuracionRecetas?.cargarRedesClinica();

          // Cargamos los datos del formulario
          this.cargarDatosClinica();
          this.cargarLogoClinica();
        },

        error: (error) => {
          // console.error('Error al obtener la institución:', error);

          Swal.fire({
            icon: 'error',
            title: 'No se pudo cargar la institución',
            text:
              error?.error?.message ??
              'Ocurrió un error al obtener la información de la institución.',
            showConfirmButton: false,
            timer: 2000,
          });
        },
      });
    }
  }

  cargarRedesSociales(): void {
    this.clinicaService.obtenerClinica().subscribe({
      next: (clinica) => {
        this.redSocialAgregada = clinica.redesSociales ?? [];
      },
      error: (error) => {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error?.error?.message ?? 'No se pudieron cargar las redes sociales.',
          showConfirmButton: false,
          timer: 2000,
        });
      },
    });
  }

  cargarDatosClinica(): void {
    if (!this.clinica) return;

    this.clinicaForm.patchValue({
      nombre: this.clinica.nombre ?? '',
      direccion: this.clinica.direccion ?? '',
      claveClinica: String(this.clinica.claveClinica ?? ''),
    });
  }

  cargarIndicaciones(): void {
    if (this.user?.rol?.nombre === 'MEDIC@' || this.user?.rol?.nombre === 'ADMIN') {
      this.indicacionService.obtenerIndicaciones().subscribe({
        next: (indicaciones) => {
          // console.log('Indicaciones obtenidas:', indicaciones);
          this.indicacionesAgregadas = indicaciones;
        },
        error: (error) => {
          // console.error('Error al obtener las indicaciones:', error);

          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: error?.error?.message ?? 'No se pudieron cargar las indicaciones.',
            showConfirmButton: false,
            timer: 2000,
          });
        },
      });
    }
  }

  cargarLogoClinica(): void {
    this.clinicaService.obtenerArchivoLogo().subscribe({
      next: (blob) => {
        // console.log('Logo obtenido del backend:', blob);

        // Liberar la URL anterior si existía
        if (this.logoClinica) {
          URL.revokeObjectURL(this.logoClinica);
        }

        // Crear una URL temporal para mostrar la imagen
        this.logoClinica = URL.createObjectURL(blob);
      },
      error: (error) => {
        // console.error('Error al obtener el logo:', error);
        this.logoClinica = null;
      },
    });
  }

  plan: any[] = [];

  cargarPlan(): void {
    if (this.user?.rol?.nombre === 'ADMIN') {
      this.planService.obtenerPlan().subscribe({
        next: (plan) => {
          this.plan = plan;
        },
        error: (error) => {
          // console.error('Error al cargar planes:', error);

          Swal.fire({
            icon: 'error',
            title: 'Error al cargar planes',
            text: error?.error?.message ?? 'No se pudieron cargar los planes.',
            showConfirmButton: false,
            timer: 2000,
          });
        },
      });
    }
  }

  iconoPlan(p: any): string {
    // Ajusta esta lógica según cómo tu backend identifique el "tier" del plan
    switch (p.nombre?.toLowerCase()) {
      case 'prueba gratuita':
        return 'workspace_premium';
      case 'base':
        return 'stat_1';
      case 'profesional':
        return 'stat_2';
      case 'premium':
        return 'stat_3';
      default:
        return 'upgrade';
    }
  }

  cambiarPlan(p: any): void {
    // Pendiente: aquí falta el endpoint para contratar/cambiar de plan.
    // No lo veo en tu PacienteService — probablemente necesites algo como
    // this.pacienteService.cambiarPlan(p.id).subscribe(...)
    // console.log('Cambiar a plan:', p);
  }

  configuracionForm = this._formBuilder.group({
    configuracionReceta: [''],
    configuracionPlanes: [''],
    configuracionNuevasIndicaiones: [''],
  });

  altaFormGroup = this._formBuilder.group({
    cedulas: ['', Validators.required],
    nombre: ['', Validators.required],
    apellidoPaterno: ['', Validators.required],
    apellidoMaterno: ['', Validators.required],
    email: ['', [Validators.email]],
    password: ['', Validators.required],
    profesion: ['', Validators.required],
    estaActivo: ['', Validators.required],
    rolId: ['', Validators.required],
  });

  recetaFormGrup = this._formBuilder.group({});

  opciones: OpcionConfiguracion[] = [
    {
      titulo: 'Receta PDF',
      descripcion: 'Personaliza el diseño, colores, logo y marca de agua de las recetas generadas.',
      icono: 'description',
      accion: () => this.abrirConfiguracionReceta(),
    },
  ];

  abrirConfiguracionReceta(): void {
    this.dialog.open(ConfiguracionRecetas, {
      width: '800px',
      maxWidth: '95vw',
      height: '85vh',
    });
  }

  personalFormGrup = this._formBuilder.group({});
  redSocialFormGrup = this._formBuilder.group({});
  indicacionesFormGrup = this._formBuilder.group({});
  municipiosFormGrup = this._formBuilder.group({});

  personalAgregado: any[] = [];
  redSocialAgregada: RedSocial[] = [];
  indicacionesAgregadas: any[] = [];
  municipiosAgregadas: any[] = [];

  // CREAR, EDITAR Y ELIMINAR USUARIO SIENDO ADMINISTRADOR

  eliminarPersonal(index: number) {
    const personal = this.personalAgregado[index];

    if (!personal?.id) {
      // console.error('No se puede eliminar: falta el ID del usuario.');
      return;
    }

    Swal.fire({
      title: '¿Eliminar usuario?',
      text: `¿Estás seguro de eliminar a ${personal.nombre} ${personal.apellidoPaterno}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      confirmButtonColor: '#d33',
      cancelButtonText: 'Cancelar',
      reverseButtons: true,
    }).then((resultado) => {
      if (!resultado.isConfirmed) {
        return;
      }

      this.usuarioService.eliminarUsuario(personal.id).subscribe({
        next: () => {
          Swal.fire({
            icon: 'success',
            title: 'Usuario eliminado',
            showConfirmButton: false,
            timer: 1500,
          });

          this.cargarPersonal();
        },
        error: (error) => {
          // console.error('Error al eliminar usuario:', error);

          Swal.fire({
            icon: 'error',
            title: 'No se pudo eliminar',
            text: error?.error?.message ?? 'Ocurrió un error al eliminar el usuario.',
            showConfirmButton: false,
            timer: 2000,
          });
        },
      });
    });
  }

  editarPersonal(index: number) {
    const personal = this.personalAgregado[index];

    this.abrirDialogPersonal(personal, index);
  }

  abrirDialogPersonal(personal?: any, index?: number) {
    const dialogRef = this.dialog.open(AltaPersonalDialog, {
      width: '800px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: {
        personal: personal,
        index: index,
      },
    });

    dialogRef.afterClosed().subscribe((resultado) => {
      if (!resultado) {
        return;
      }

      this.cargarPersonal();
    });
  }

  // ! NUEVAS INDICACIONES

  eliminarIndicaciones(index: number) {
    const indicacion = this.indicacionesAgregadas[index];

    if (!indicacion?.id) {
      // console.error('No se puede eliminar.');
      return;
    }

    Swal.fire({
      title: '¿Eliminar indicación?',
      text: `¿Estás seguro de eliminar la indicación "${indicacion.titulo}?"`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      confirmButtonColor: '#d33',
      cancelButtonText: 'Cancelar',
      reverseButtons: true,
    }).then((resultado) => {
      if (!resultado.isConfirmed) {
        return;
      }

      this.indicacionService.eliminarIndicacion(indicacion.id).subscribe({
        next: () => {
          Swal.fire({
            icon: 'success',
            title: 'Indicación eliminado',
            showConfirmButton: false,
            timer: 1500,
          });

          this.cargarIndicaciones();
        },
        error: (error) => {
          // console.error('Error al eliminar la indicación:', error);

          Swal.fire({
            icon: 'error',
            title: 'No se pudo eliminar',
            text: error?.error?.message ?? 'Ocurrió un error al eliminar la indicación.',
            showConfirmButton: false,
            timer: 2000,
          });
        },
      });
    });
  }

  editarIndicaciones(index: number) {
    const indicacion = this.indicacionesAgregadas[index];

    this.abrirDialogIndicaciones(indicacion, index);
  }

  abrirDialogIndicaciones(indicacion?: any, index?: number) {
    const dialogRef = this.dialog.open(NuevaIndicacionDialog, {
      width: '500px',
      data: {
        indicacion: indicacion,
        index: index,
      },
    });

    dialogRef.afterClosed().subscribe((resultado) => {
      if (!resultado) {
        return;
      }

      // Si existe índice → estamos editando
      if (index !== undefined) {
        this.indicacionesAgregadas[index] = resultado;
      } else {
        // Si no existe índice → estamos agregando
        this.indicacionesAgregadas.push(resultado);
      }
    });
  }

  // ! NUEVOS MUNICIPIOS

  cargarMunicipios(): void {
    if (this.user?.rol?.nombre !== 'ADMIN') return;

    this.municipioService.obtenerMunicipio().subscribe({
      next: (respuesta: any) => {
        this.municipiosAgregadas = Array.isArray(respuesta) ? respuesta : (respuesta?.data ?? []);
      },
      error: (error) => {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error?.error?.message ?? 'No se pudieron cargar los municipios.',
          showConfirmButton: false,
          timer: 2000,
        });
      },
    });
  }

  eliminarMunicipio(index: number) {
    const municipio = this.municipiosAgregadas[index];

    if (!municipio?.id) return;

    Swal.fire({
      title: '¿Eliminar municipio?',
      text: `¿Estás seguro de eliminar el municipio "${municipio.nombre}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      confirmButtonColor: '#d33',
      cancelButtonText: 'Cancelar',
      reverseButtons: true,
    }).then((resultado) => {
      if (!resultado.isConfirmed) return;

      this.municipioService.eliminarMunicipio(municipio.id).subscribe({
        next: () => {
          Swal.fire({
            icon: 'success',
            title: 'Municipio eliminado',
            showConfirmButton: false,
            timer: 1500,
          });

          this.cargarMunicipios();
        },
        error: (error) => {
          Swal.fire({
            icon: 'error',
            title: 'No se pudo eliminar',
            text: error?.error?.message ?? 'Ocurrió un error al eliminar el municipio.',
          });
        },
      });
    });
  }

  editarMunicipio(index: number) {
    this.abrirDialogMunicipio(this.municipiosAgregadas[index]);
  }

  abrirDialogMunicipio(municipio?: any) {
    const dialogRef = this.dialog.open(NuevoMunicipioDialog, {
      width: '500px',
      data: { municipio },
    });

    dialogRef.afterClosed().subscribe((resultado) => {
      if (resultado) this.cargarMunicipios();
    });
  }

  abrirBarrios(municipio: any) {
    this.dialog.open(BarriosMunicipioDialog, {
      width: '800px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: { municipio },
    });
  }

  // ! NUEVA RED SOCIAL

  eliminarRedSocial(index: number) {
    const redSocial = this.redSocialAgregada[index];

    if (!redSocial?.id) return;

    Swal.fire({
      title: '¿Eliminar red social?',
      text: `¿Estás seguro de eliminar "${redSocial.redSocial}" (${redSocial.usuario})?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      confirmButtonColor: '#d33',
      cancelButtonText: 'Cancelar',
      reverseButtons: true,
    }).then((resultado) => {
      if (!resultado.isConfirmed) return;

      this.redSocialService.eliminarRedesSociales(redSocial.id).subscribe({
        next: () => {
          Swal.fire({
            icon: 'success',
            title: 'Red social eliminada',
            showConfirmButton: false,
            timer: 1500,
          });
          this.cargarRedesSociales();
        },
        error: (error) => {
          Swal.fire({
            icon: 'error',
            title: 'No se pudo eliminar',
            text: error?.error?.message ?? 'Ocurrió un error al eliminar la red social.',
          });
        },
      });
    });
  }

  editarRedSocial(index: number) {
    this.abrirDialogRedSocial(this.redSocialAgregada[index]);
  }

  abrirDialogRedSocial(redSocial?: RedSocial) {
    const dialogRef = this.dialog.open(RedesSocialesDialog, {
      width: '500px',
      data: { redSocial },
    });

    dialogRef.afterClosed().subscribe((resultado) => {
      if (resultado) this.cargarRedesSociales();
    });
  }

  // ! EDITAR PERFIL

  mostrarPasswordActual = false;
  mostrarPasswordNueva = false;
  mostrarPasswordConfirmar = false;

  private fb = inject(FormBuilder);

  perfilForm = this.fb.group({
    nombre: ['', Validators.required],
    apellidoPaterno: ['', Validators.required],
    apellidoMaterno: ['', Validators.required],
    cedulas: new FormArray<FormGroup>([], cedulasSinDuplicados),
  });

  get cedulasPerfil(): FormArray<FormGroup> {
    return this.perfilForm.controls.cedulas;
  }

  private crearGrupoCedula(c?: {
    id?: string | number;
    tipoCedula?: string;
    cedula?: string | number;
  }) {
    return this.fb.group({
      id: [(c?.id ?? null) as string | number | null],
      tipoCedula: [c?.tipoCedula ?? 'Licenciatura', Validators.required],
      cedula: [String(c?.cedula ?? ''), [Validators.required, Validators.pattern(PATRON_CEDULA)]],
    });
  }

  agregarCedulaPerfil(): void {
    this.cedulasPerfil.push(this.crearGrupoCedula());
  }

  quitarCedulaPerfil(index: number): void {
    const grupo = this.cedulasPerfil.at(index);
    const id = grupo.get('id')?.value as string | number | null;

    // Cédula nueva, todavía no existe en el backend
    if (id == null) {
      this.cedulasPerfil.removeAt(index);
      return;
    }

    const numero = grupo.get('cedula')?.value;

    Swal.fire({
      icon: 'warning',
      title: '¿Eliminar cédula?',
      text: `Se eliminará la cédula ${numero} de tu perfil.`,
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      reverseButtons: true,
    }).then((result) => {
      if (!result.isConfirmed) return;

      this.cedulaService.eliminarCedula(id).subscribe({
        next: () => {
          // Se busca de nuevo el índice por si la lista cambió
          const posicion = this.cedulasPerfil.controls.indexOf(grupo);
          if (posicion >= 0) this.cedulasPerfil.removeAt(posicion);

          this.cedulasPerfilOriginales.delete(id);

          // Actualiza la sección "Cédulas profesionales" de solo lectura
          this.user = {
            ...this.user,
            cedulas: (this.user?.cedulas ?? []).filter((c: any) => c.id !== id),
          };

          Swal.fire({
            icon: 'success',
            title: 'Cédula eliminada',
            timer: 1500,
            showConfirmButton: false,
          });
        },
        error: (error) => {
          Swal.fire({
            icon: 'error',
            title: 'No se pudo eliminar',
            text: error?.error?.message ?? 'Ocurrió un error al eliminar la cédula.',
            showConfirmButton: false,
            timer: 2000,
          });
        },
      });
    });
  }

  passwordForm = this.fb.group({
    password: ['', Validators.required],
    passwordNew: ['', [Validators.required, Validators.minLength(8)]],
    passwordConfirm: ['', [Validators.required, Validators.minLength(8)]],
  });

  guardarPerfil(): void {
    if (this.perfilForm.invalid) {
      this.perfilForm.markAllAsTouched();
      return;
    }

    const valores = this.perfilForm.getRawValue();

    const datos = {
      nombre: valores.nombre?.trim(),
      apellidoPaterno: valores.apellidoPaterno?.trim(),
      apellidoMaterno: valores.apellidoMaterno?.trim(),
    };

    const cedulas = (valores.cedulas ?? []).map((c: any) => ({
      id: c.id as string | number | null,
      tipoCedula: c.tipoCedula as string,
      cedula: String(c.cedula ?? '').trim(),
    }));

    Swal.fire({
      icon: 'question',
      title: '¿Guardar cambios?',
      text: '¿Estás seguro de que deseas actualizar tu información personal y tus cédulas?',
      showCancelButton: true,
      confirmButtonText: 'Sí, guardar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#24575A',
      cancelButtonColor: '#6c757d',
      reverseButtons: true,
    }).then((result) => {
      if (!result.isConfirmed) return;

      const peticiones: Observable<any>[] = [this.usuarioService.actualizarUsuarioNombre(datos)];

      for (const item of cedulas) {
        if (item.id != null) {
          const original = this.cedulasPerfilOriginales.get(item.id);
          const cambios: ActualizarCedulaRequest = {};

          if (original?.cedula !== item.cedula) cambios.cedula = Number(item.cedula);
          if (original?.tipoCedula !== item.tipoCedula) cambios.tipoCedula = item.tipoCedula;

          if (Object.keys(cambios).length > 0) {
            peticiones.push(this.cedulaService.actualizarCedula(item.id, cambios));
          }
        } else {
          peticiones.push(
            this.cedulaService.crearCedula({
              tipoCedula: item.tipoCedula,
              cedula: Number(item.cedula),
            }),
          );
        }
      }

      forkJoin(peticiones).subscribe({
        next: () => {
          this.user = { ...this.user, ...datos };
          this.authService.actualizarUsuarioLocal(datos);

          // Recarga para obtener los ids de las cédulas recién creadas
          this.usuarioService.obtenerUsuario(this.user.id).subscribe((u) => {
            this.user = u;
            this.cargarDatosPerfil();
          });

          Swal.fire({
            icon: 'success',
            title: 'Perfil actualizado',
            text: 'Tus datos y cédulas se actualizaron correctamente.',
            showConfirmButton: false,
            timer: 2000,
          });
        },
        error: (error) => {
          // console.error('Error al actualizar el perfil:', error);
          Swal.fire({
            icon: 'error',
            title: 'No se pudo actualizar',
            text: error?.error?.message ?? 'Ocurrió un error al actualizar tus datos.',
            showConfirmButton: false,
            timer: 2000,
          });
        },
      });
    });
  }

  cambiarPassword(): void {
    if (this.passwordForm.invalid) {
      this.passwordForm.markAllAsTouched();
      return;
    }

    const valores = this.passwordForm.getRawValue();

    // Verificar que las contraseñas nuevas coincidan
    if (valores.passwordNew !== valores.passwordConfirm) {
      this.passwordForm.get('passwordConfirm')?.setErrors({
        passwordMismatch: true,
      });

      this.passwordForm.get('passwordConfirm')?.markAsTouched();
      return;
    }

    // Confirmación antes de realizar el PATCH
    Swal.fire({
      icon: 'question',
      title: '¿Cambiar contraseña?',
      text: '¿Estás seguro de que deseas cambiar tu contraseña?',
      showCancelButton: true,
      confirmButtonText: 'Sí, cambiar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#24575A',
      cancelButtonColor: '#6c757d',
      reverseButtons: true,
    }).then((result) => {
      // Si cancela, no hacemos nada
      if (!result.isConfirmed) {
        return;
      }

      const payload = {
        password: valores.password!,
        passwordNew: valores.passwordNew!,
      };

      // console.log('Datos enviados al backend:', payload);

      this.usuarioService.actualizarUsuarioPassword(payload).subscribe({
        next: () => {
          Swal.fire({
            icon: 'success',
            title: 'Contraseña actualizada',
            text: 'Tu contraseña se cambió correctamente.',
            showConfirmButton: false,
            timer: 2000,
          });

          // Limpiar formulario
          this.passwordForm.reset();

          // Ocultar contraseñas
          this.mostrarPasswordActual = false;
          this.mostrarPasswordNueva = false;
          this.mostrarPasswordConfirmar = false;
        },

        error: (error) => {
          // console.error('Error al cambiar la contraseña:', error);

          Swal.fire({
            icon: 'error',
            title: 'No se pudo cambiar la contraseña',
            text: error?.error?.message ?? 'La contraseña actual es incorrecta o ocurrió un error.',
            showConfirmButton: false,
            timer: 2000,
          });
        },
      });
    });
  }

  //EDITAR FORMATO DE EXPEDIENTE

  expedienteForm!: FormGroup;

  configuracionExpediente: any = null;

  guardandoExpediente = false;

  guardarConfiguracionExpediente(): void {
    if (this.expedienteForm.invalid) {
      this.expedienteForm.markAllAsTouched();
      return;
    }

    Swal.fire({
      icon: 'question',
      title: '¿Guardar cambios?',
      text: '¿Estás seguro de que deseas actualizar el formato del expediente?',
      showCancelButton: true,
      confirmButtonText: 'Sí, guardar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#24575A',
      cancelButtonColor: '#6c757d',
      reverseButtons: true,
    }).then((result) => {
      // El usuario canceló
      if (!result.isConfirmed) {
        return;
      }

      this.guardandoExpediente = true;

      const payload: ActualizarExpedienteConfiguracion = {
        formato: this.expedienteForm.get('formato')?.value,
        prefijo: this.expedienteForm.get('prefijo')?.value,
        longitud: Number(this.expedienteForm.get('longitud')?.value),
      };

      // console.log('Payload enviado al backend:', payload);

      this.expedienteConfiguracionService.actualizarExpediente(payload).subscribe({
        next: (respuesta) => {
          // Mostrar respuesta completa del backend
          // console.log('Respuesta del backend:', respuesta);

          this.configuracionExpediente = respuesta;

          this.guardandoExpediente = false;

          Swal.fire({
            icon: 'success',
            title: 'Configuración actualizada',
            text: 'El formato del expediente se actualizó correctamente.',
            showConfirmButton: false,
            timer: 2000,
          });
        },

        error: (error) => {
          // console.error('Error al actualizar expediente:', error);

          this.guardandoExpediente = false;

          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'No se pudo actualizar la configuración del expediente.',
            showConfirmButton: false,
            timer: 2000,
          });
        },
      });
    });
  }

  vistaPreviaExpediente(): string {
    if (!this.expedienteForm) {
      return '';
    }

    const prefijo = this.expedienteForm.get('prefijo')?.value || 'PAC';

    const longitud = Number(this.expedienteForm.get('longitud')?.value) || 6;

    const formato =
      this.expedienteForm.get('formato')?.value || '{PREFIJO}-{YY}-{MM}-{CONSECUTIVO}';

    const fecha = new Date();

    const yy = fecha.getFullYear().toString().slice(-2);

    const mm = String(fecha.getMonth() + 1).padStart(2, '0');

    const consecutivo = String(9).padStart(longitud, '0');

    return formato
      .replace(/\{PREFIJO\}/g, prefijo)
      .replace(/\{YY\}/g, yy)
      .replace(/\{MM\}/g, mm)
      .replace(/\{CONSECUTIVO\}/g, consecutivo);
  }

  // ===================================
  // EDITAR INFORMACIÓN DE LA CLÍNICA
  // ===================================

  clinicaForm = this.fb.group({
    nombre: ['', Validators.required],
    direccion: ['', Validators.required],
    claveClinica: ['', [Validators.required, Validators.pattern(/^[1-9]\d*$/)]],
  });

  clinica!: Clinica;
  suscripcionActual?: Subscripcion;
  configuracionClinica?: ConfiguracionClinica;
  logoClinica: string | null = null;
  archivoLogoSeleccionado: File | null = null;
  vistaPreviaLogo: string | null = null;
  subiendoLogo = false;

  guardarClinica(): void {
    if (this.clinicaForm.invalid) {
      this.clinicaForm.markAllAsTouched();
      return;
    }

    const valores = this.clinicaForm.getRawValue();

    const datos: ActualizarClinica = {
      nombre: valores.nombre?.trim() ?? '',
      direccion: valores.direccion?.trim() ?? '',
      claveClinica: String(valores.claveClinica ?? '').trim(),
    };

    Swal.fire({
      title: '¿Guardar cambios?',
      text: 'Se actualizará la información de la institución.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, guardar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#24575A',
      reverseButtons: true,
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }

      this.clinicaService.actualizarClinica(datos).subscribe({
        next: (respuesta) => {
          const clinicaActualizada = respuesta.data ?? datos;

          // Actualizar los datos de la clínica en Configuraciones
          this.clinica = {
            ...this.clinica,
            ...clinicaActualizada,
          };

          // Obtener el usuario actual
          const usuarioActual = this.authService.getUser();

          if (usuarioActual) {
            // Actualizar la clínica dentro del usuario
            const usuarioActualizado = {
              ...usuarioActual,
              clinica: {
                ...usuarioActual.clinica,
                ...clinicaActualizada,
              },
            };

            // Notificar el cambio a los componentes suscritos
            this.authService.actualizarUsuarioCompleto(usuarioActualizado);
          }

          Swal.fire({
            icon: 'success',
            title: 'Información actualizada',
            text: respuesta.mensaje || 'Los datos de la institución se actualizaron correctamente.',
            showConfirmButton: false,
            timer: 1800,
          });
        },
        error: (error) => {
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text:
              error?.error?.message?.join?.(', ') ??
              error?.error?.message ??
              'No fue posible actualizar la información de la clínica.',
            showConfirmButton: false,
            timer: 2000,
          });
        },
      });
    });
  }

  seleccionarLogo(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (!input.files || input.files.length === 0) {
      return;
    }

    const archivo = input.files[0];

    // Validar tipo
    if (!archivo.type.startsWith('image/')) {
      Swal.fire({
        icon: 'error',
        title: 'Archivo no válido',
        text: 'Selecciona una imagen válida.',
        showConfirmButton: false,
        timer: 2000,
      });

      input.value = '';
      return;
    }

    // Validar tamaño: 5 MB
    const maxSize = 5 * 1024 * 1024;

    if (archivo.size > maxSize) {
      Swal.fire({
        icon: 'error',
        title: 'Archivo demasiado grande',
        text: 'El logo no puede superar los 5 MB.',
        showConfirmButton: false,
        timer: 2000,
      });

      input.value = '';
      return;
    }

    this.archivoLogoSeleccionado = archivo;

    // Vista previa
    const reader = new FileReader();

    reader.onload = () => {
      this.vistaPreviaLogo = reader.result as string;
    };

    reader.readAsDataURL(archivo);
  }

  async subirLogo(): Promise<void> {
    if (!this.archivoLogoSeleccionado) {
      return;
    }

    Swal.fire({
      title: '¿Actualizar logo?',
      text: 'El logo actual será reemplazado.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, actualizar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#24575A',
      cancelButtonColor: '#6c757d',
      reverseButtons: true,
    }).then(async (result) => {
      if (!result.isConfirmed) {
        return;
      }

      this.subiendoLogo = true;

      this.clinicaService.subirArchivoLogo(this.archivoLogoSeleccionado!).subscribe({
        next: async (respuesta) => {
          // console.log('Logo subido:', respuesta);

          try {
            // ==========================================
            // 1. Recargar logo en "Editar institución"
            // ==========================================
            await this.cargarLogoClinica();

            // ==========================================
            // 2. Obtener el nuevo logo desde el backend
            // ==========================================
            const blob = await firstValueFrom(this.clinicaService.obtenerArchivoLogo());

            // ==========================================
            // 3. Actualizar logo utilizado por RecetasPdf
            // ==========================================
            await this.recetasPdf.establecerLogoDesdeBlob(blob);

            // ==========================================
            // 4. Actualizar inmediatamente
            //    "Personalizar receta"
            // ==========================================
            await this.configuracionRecetas?.cargarLogoInstitucion();

            await this.recetasPdf.establecerLogoDesdeBlob(blob);

            // console.log('Logo actualizado en toda la configuración.');
          } catch (error) {
            // console.error('El logo se subió, pero no se pudo actualizar completamente:', error);
          } finally {
            this.subiendoLogo = false;
            this.archivoLogoSeleccionado = null;
            this.vistaPreviaLogo = null;
          }

          Swal.fire({
            icon: 'success',
            title: 'Logo actualizado',
            text: 'El logo de la institución se actualizó correctamente.',
            showConfirmButton: false,
            timer: 2000,
          });
        },

        error: (error) => {
          // console.error('Error al subir el logo:', error);

          this.subiendoLogo = false;

          Swal.fire({
            icon: 'error',
            title: 'No se pudo actualizar el logo',
            text:
              error?.error?.message?.join?.(', ') ??
              error?.error?.message ??
              'Ocurrió un error al subir el logo.',
            showConfirmButton: false,
            timer: 2000,
          });
        },
      });
    });
  }
}
